import { BadRequestError, UnknownError } from '@nftx/errors';
import config from '@nftx/config';
import { query, getUniqueTokenIds, getTokenIdAmounts } from '@nftx/utils';

// Wraps an async function.
// While the promise is unresolved, all subsequent calls will receive the same promise
const throttleAsyncFn = fn => {
  let p;
  return (...args) => {
    if (p) {
      return p;
    }
    p = fn(...args).then(result => {
      p = undefined;
      return result;
    }, e => {
      p = undefined;
      throw e;
    });
    return p;
  };
};

/** Gets the required block number */
const getRequiredBlockNumber = (network = config.network) => {
  return config.internal.requiredBlockNumber[network] ?? 0;
};

/** Sets the required block number */
const setRequiredBlockNumber = (value, network = config.network) => {
  config.internal.requiredBlockNumber[network] = value;
};

/** Gets the current block number from the api */
const getApiBlockNumber = (network = config.network) => {
  return config.internal.apiBlockNumber[network] ?? 0;
};
const getBlockBuffer = () => {
  return config.internal.blockBuffer;
};

/** Get the block number that the api is currently on */
const fetchApiBlock = async (network = config.network) => {
  const response = await query({
    url: `${config.urls.NFTX_API_URL}/${network}/block`,
    query: {
      source: 'live',
      ebn: 'true'
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: config.keys.NFTX_API
    }
  });
  const block = response?.block ?? 0;
  return block;
};

// Checks whether the required block number is ahead of the api block number
const isApiBehind = ({
  network,
  requiredBlockNumber
}) => {
  if (!requiredBlockNumber) {
    // No required block number so we don't need to worry about the api
    return false;
  }
  const apiBlockNumber = getApiBlockNumber(network);
  return apiBlockNumber < requiredBlockNumber;
};
const updateLastIndexedBlock = async ({
  network
}) => {
  // Get the last indexed block on the api
  const block = await fetchApiBlock(network);
  // Save it (writes to local storage)
  config.internal.apiBlockNumber[network] = block;
};
const resetRequiredBlock = ({
  network
}) => {
  // reset the required block number
  setRequiredBlockNumber(0, network);
  // Switch back to using the api as the SoT
  config.internal.source = 'api';
};

/** Checks if the api is behind the latest block.
 * If it is behind, it will switch to live mode and continue checking the api until it has caught up
 **/
const syncApiBlock = throttleAsyncFn(async (network = config.network) => {
  // We throttle this method so even if 1k requests are made in quick succession,
  // we'll only attempt to sync the api one time

  let wasBehind = false;
  // Keep looping while the api is behind the current block
  while (isApiBehind({
    network,
    requiredBlockNumber: getRequiredBlockNumber(network)
  })) {
    wasBehind = true;
    if (config.internal.source !== 'live') {
      // Switch to live mode
      config.internal.source = 'live';
    }
    // Wait 5s before polling again
    await new Promise(res => setTimeout(res, 5000));
    // Fetch the latest block from the api
    await updateLastIndexedBlock({
      network
    });
  }
  if (wasBehind) {
    // The api has caught up and we no longer need to be in live mode
    resetRequiredBlock({
      network
    });
  }
});

var nsync = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getRequiredBlockNumber: getRequiredBlockNumber,
  setRequiredBlockNumber: setRequiredBlockNumber,
  getApiBlockNumber: getApiBlockNumber,
  getBlockBuffer: getBlockBuffer,
  fetchApiBlock: fetchApiBlock,
  syncApiBlock: syncApiBlock
});

const queryApi = async ({
  url,
  query: givenQuery = {},
  method = 'GET'
}) => {
  // Make sure the api is up to date (or switch to live mode if necessary)
  syncApiBlock();
  const uri = new URL(url, config.urls.NFTX_API_URL);
  const query$1 = {
    ...givenQuery,
    ebn: 'true',
    source: config.internal.source
  };
  // Add the api key header
  const headers = {
    'Content-Type': 'application/json'
  };
  if (config.keys.NFTX_API) {
    headers['Authorization'] = config.keys.NFTX_API;
  } else {
    console.warn('No NFTX API key found');
  }

  // Send the request
  const data = await query({
    url: uri.toString(),
    method,
    headers,
    query: query$1
  });
  return data;
};
var queryApi$1 = queryApi;

const fetchAsset = ({
  assetAddress,
  network,
  tokenId
}) => {
  return queryApi$1({
    url: `/${network}/assets/${assetAddress}/${tokenId}`
  });
};
var fetchAsset$1 = fetchAsset;

class Stream {
  constructor() {
    this.reading = false;
    this.errored = false;
    this.ended = false;
    this.listeners = {};
  }
  on(e, cb) {
    this.listeners[e] = cb;
    if (e === 'data') {
      this.reading = true;
      this.listeners.read?.();
    }
  }
  read() {
    return new Promise((res, rej) => {
      if (this.listeners.data) {
        return rej(new Error('stream is automatically reading as there is a data listener'));
      }
      if (this.ended) {
        return rej(new Error('stream has ended'));
      }
      if (this.reading) {
        return rej(new Error('stream is already reading'));
      }
      const onEnd = this.listeners.end;
      const onError = this.listeners.error;
      const revert = () => {
        this.reading = false;
        this.listeners.data = undefined;
        this.listeners.error = onError;
        this.listeners.end = onEnd;
      };
      this.listeners.data = data => {
        revert();
        res(data);
      };
      this.listeners.error = e => {
        revert();
        onError?.(e);
        rej(e);
      };
      this.listeners.end = () => {
        revert();
        onEnd?.();
      };
      this.reading = true;
      this.listeners.read?.();
    });
  }
  write(data) {
    if (this.ended) {
      return;
    }
    this.listeners.data?.(data);
    if (this.listeners.data) {
      this.listeners.read?.();
    }
  }
  error(e) {
    if (!this.ended) {
      this.errored = true;
      this.ended = true;
      this.listeners.error?.(e);
    }
  }
  end(data) {
    if (this.ended) {
      return;
    }
    this.listeners.data?.(data);
    this.ended = true;
    this.reading = false;
    this.listeners.end?.();
  }
}

function fetchAssets({
  assetAddress,
  userAddress,
  vaultId,
  mintable,
  network = config.network,
  cursor
}) {
  const url = `/${network}/assets`;
  const query = {
    cursor,
    userAddress,
    vaultId,
    mintable,
    assetAddress
  };
  return queryApi$1({
    url,
    query
  });
}

/** Stream assets owned by a user */

/** Stream assets for a specific vault */

/** Stream assets for all vaults for a specific collection */

function streamAssets({
  network = config.network,
  assetAddress,
  mintable,
  userAddress,
  vaultId
}) {
  let cursor;
  const stream = new Stream();
  stream.on('read', async () => {
    try {
      const result = await (() => {
        if (userAddress) {
          return fetchAssets({
            userAddress,
            assetAddress,
            cursor,
            mintable,
            network,
            vaultId
          });
        }
        if (vaultId) {
          return fetchAssets({
            vaultId,
            cursor,
            network
          });
        }
        if (assetAddress) {
          return fetchAssets({
            assetAddress,
            cursor,
            network
          });
        }
        throw new BadRequestError('Must be provide either userAddress, vaultId, or assetAddress');
      })();
      cursor = result.cursor;
      if (cursor) {
        stream.write(result.assets);
      } else {
        stream.end(result.assets);
      }
    } catch (e) {
      stream.error(e);
    }
  });
  return stream;
}

function fetchAllAssets({
  assetAddress,
  userAddress,
  vaultId,
  mintable,
  network
}) {
  return new Promise((res, rej) => {
    const allAssets = [];
    const stream = (() => {
      if (userAddress) {
        return streamAssets({
          userAddress,
          assetAddress,
          mintable,
          network,
          vaultId
        });
      }
      if (vaultId) {
        return streamAssets({
          vaultId,
          network
        });
      }
      if (assetAddress) {
        return streamAssets({
          assetAddress,
          network
        });
      }
      throw new BadRequestError('Must provide userAddress, vaultId, or assetAddress');
    })();
    stream.on('error', rej);
    stream.on('end', () => res(allAssets));
    stream.on('data', data => {
      allAssets.push(...data);
    });
  });
}

const fetchAuctions = ({
  network = config.network
} = {}) => {
  const url = `/${network}/auctions`;
  return queryApi$1({
    url
  });
};
var fetchAuctions$1 = fetchAuctions;

const fetchInventoryPool = ({
  network = config.network,
  vaultId
}) => {
  return queryApi$1({
    url: `/${network}/pools/inventory/${vaultId}`
  });
};
var fetchInventoryPool$1 = fetchInventoryPool;

const fetchInventoryPools = async ({
  network = config.network,
  vaultAddresses,
  vaultIds
}) => {
  return queryApi$1({
    url: `/${network}/pools/inventory`,
    query: {
      vaultAddresses,
      vaultIds
    }
  });
};
var fetchInventoryPools$1 = fetchInventoryPools;

/** Get a specific liquidity pool */
const fetchLiquidityPool = ({
  network = config.network,
  poolId
}) => {
  return queryApi$1({
    url: `/${network}/pools/liquidity/${poolId}`
  });
};
var fetchLiquidityPool$1 = fetchLiquidityPool;

/** Get all liquidity pools that match the given criteria. If no arguments are given, it will return all pools */
const fetchLiquidityPools = ({
  network = config.network,
  poolIds,
  vaultAddress,
  vaultAddresses,
  vaultId,
  vaultIds,
  exists
} = {}) => {
  return queryApi$1({
    url: `/${network}/pools/liquidity`,
    query: {
      liquidityPoolId: poolIds,
      id: vaultAddress ?? vaultAddresses,
      vaultId: vaultId ?? vaultIds,
      exists
    }
  });
};
var fetchLiquidityPools$1 = fetchLiquidityPools;

const fetchInventoryPosition = ({
  network = config.network,
  positionId
}) => {
  return queryApi$1({
    url: `/${network}/positions/inventory/${positionId}`
  });
};
var fetchInventoryPosition$1 = fetchInventoryPosition;

const fetchInventoryPositions = ({
  network = config.network,
  userAddress,
  vaultId
}) => {
  const url = `/${network}/positions/inventory`;
  const query = {
    userAddress,
    vaultId
  };
  return queryApi$1({
    url,
    query
  });
};
var fetchInventoryPositions$1 = fetchInventoryPositions;

/** Get a position for specific user and vault */
const fetchLiquidityPosition = ({
  network = config.network,
  positionId
}) => {
  return queryApi$1({
    url: `/${network}/positions/liquidity/${positionId}`
  });
};
var fetchLiquidityPosition$1 = fetchLiquidityPosition;

const fetchLiquidityPositions = ({
  network = config.network,
  userAddress,
  vaultId,
  poolId,
  tokenId
}) => {
  const url = `/${network}/positions/liquidity`;
  const query = {
    userAddress,
    vaultId,
    poolId,
    tokenId
  };
  return queryApi$1({
    url,
    query
  });
};
var fetchLiquidityPositions$1 = fetchLiquidityPositions;

/** Returns an on-chain quote for a transaction. The response object can be passed into @nftx/trade's fulfill method to execute the quote */

function fetchQuote(args) {
  const {
    type,
    quoteType = 'quote',
    network = config.network,
    vaultId,
    userAddress,
    slippagePercentage
  } = args;
  const url = `/${network}/quote/${type}`;
  const query = {
    vaultId,
    userAddress,
    slippagePercentage
  };
  switch (type) {
    case 'buy':
    case 'redeem':
      query.buyTokenIds = getUniqueTokenIds(args.tokenIds);
      query.buyAmounts = getTokenIdAmounts(args.tokenIds);
      break;
    case 'sell':
    case 'mint':
      query.sellTokenIds = getUniqueTokenIds(args.tokenIds);
      query.sellAmounts = getTokenIdAmounts(args.tokenIds);
      break;
    case 'swap':
      query.sellTokenIds = getUniqueTokenIds(args.mintTokenIds);
      query.sellAmounts = getTokenIdAmounts(args.mintTokenIds);
      query.buyTokenIds = getUniqueTokenIds(args.redeemTokenIds);
      query.buyAmounts = getTokenIdAmounts(args.redeemTokenIds);
      break;
    case 'erc20':
      query.sellToken = args.sellToken;
      query.sellAmount = args.sellAmount;
      query.buyToken = args.buyToken;
      query.buyAmount = args.buyAmount;
      break;
  }
  if (args.permit2 && quoteType === 'quote') {
    query.permit2 = args.permit2;
  }
  const method = quoteType === 'price' ? 'GET' : 'POST';
  return queryApi$1({
    url,
    query,
    method
  });
}

/** Returns an off-chain price for a transaction */

function fetchPrice(args) {
  return fetchQuote({
    ...args,
    quoteType: 'price'
  });
}

let Network = /*#__PURE__*/function (Network) {
  Network[Network["Mainnet"] = 1] = "Mainnet";
  Network[Network["Arbitrum"] = 42161] = "Arbitrum";
  Network[Network["Sepolia"] = 11155111] = "Sepolia";
  Network[Network["Base"] = 8453] = "Base";
  return Network;
}({});
const a = s => s;

/** The address for WETH across networks */
({
  [Network.Mainnet]: a('0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2'),
  [Network.Arbitrum]: a('0x82af49447d8a07e3bd95bd0d56f35241523fbab1'),
  [Network.Sepolia]: a('0xfFf9976782d46CC05630D1f6eBAb18b2324d6B14'),
  [Network.Base]: a('0x4200000000000000000000000000000000000006')
});

/** The address for USDC across networks */
({
  [Network.Mainnet]: a('0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48'),
  [Network.Arbitrum]: a('0xff970a61a04b1ca14834a43f5de4533ebddb5cc8'),
  [Network.Sepolia]: a('0x2C032Aa43D119D7bf4Adc42583F1f94f3bf3023a'),
  [Network.Base]: a('0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913')
});

/** Zap specifically for creating new vaults - CreateVaultZap */
({
  [Network.Mainnet]: a('0x56dab32697B4A313f353DA0CE42B5113eD8E6f74'),
  [Network.Sepolia]: a('0xD80b916470F8e79FD8d09874cb159CbB8D13d8da'),
  [Network.Arbitrum]: a('0xF9E891AB1ECa89B7A4B3cBD45aEBFDF3Ec38946F'),
  [Network.Base]: a('0x2D77756C139ed3c25472Daf233F332E8F605Dd8E')
});

/** Perform buy/sell/swaps - MarketplaceUniversalRouterZap */
({
  [Network.Mainnet]: a('0x293A0c49c85F1D8851C665Ac3cE1f1DC2a79bE3d'),
  [Network.Sepolia]: a('0xd88a3B9D0Fb2d39ec8394CfFD983aFBB2D4a6410'),
  [Network.Arbitrum]: a('0xf56296B3010a59Ef7F0915569DD44E1302b9Ca40'),
  [Network.Base]: a('0x31cB832F661Cd90fc9Fca6fB70A39cA811a02aEd')
});

/** Stake NFTs or vTokens for xNFTs - NFTXInventoryStakingV3Upgradable */
({
  [Network.Mainnet]: a('0x889f313e2a3FDC1c9a45bC6020A8a18749CD6152'),
  [Network.Sepolia]: a('0xfBFf0635f7c5327FD138E1EBa72BD9877A6a7C1C'),
  [Network.Arbitrum]: a('0xe39a7E67d3E3b6eAF58BC02C4E80C3688847d155'),
  [Network.Base]: a('0x47D9ACEE6aa260C36F4368091bE92F0824876C72')
});

/** AMM Router - nftxUniversalRouter */
({
  [Network.Mainnet]: a('0x250d62a67254A46c0De472d2c9215E1d890cC90f'),
  [Network.Sepolia]: a('0x12156cCA1958B6591CC49EaE03a5553458a4b424'),
  [Network.Arbitrum]: a('0x0DA69287B4C1B28181E5d155dDDda7Fa5C32E5Ad'),
  [Network.Base]: a('0x7c656F0691Db983ee78f68189c55C36d1862c901')
});

/** Staking  / Liquidity / Pools - NftxRouter */
({
  [Network.Mainnet]: a('0x3b3e4e76cac64eb29c399dcad1f3c401d2254f5f'),
  [Network.Sepolia]: a('0x441b7DE4340AAa5aA86dB4DA43d9Badf7B2DAA66'),
  [Network.Arbitrum]: a('0x52731751Dede22827ad47109f5e9697d75a3ef4d'),
  [Network.Base]: a('0x590F8679964D4dEfB54DCD307b2941B053eb1821')
});

/** NonfungiblePositionManager */
({
  [Network.Mainnet]: a('0x26387fcA3692FCac1C1e8E4E2B22A6CF0d4b71bF'),
  [Network.Sepolia]: a('0xA9bCC1e29d3460177875f68fDCC0264D22c40BF0'),
  [Network.Arbitrum]: a('0x8AD238377531547838370B9C4aC346b9Ed5466Ea'),
  [Network.Base]: a('0x4f566a711901168804A74F252680d85C9246188e')
});

/** The permit2 contract, needs an approval signature because the UniversalRouter only transfers tokens from user via Permit2 */
({
  [Network.Mainnet]: a('0x000000000022d473030f116ddee9f6b43ac78ba3'),
  [Network.Sepolia]: a('0x000000000022d473030f116ddee9f6b43ac78ba3'),
  [Network.Arbitrum]: a('0x000000000022d473030f116ddee9f6b43ac78ba3'),
  [Network.Base]: a('0x000000000022d473030f116ddee9f6b43ac78ba3')
});

/** ENS Address */
({
  [Network.Mainnet]: a('0x57f1887a8BF19b14fC0dF6Fd9B2acc9Af147eA85'),
  [Network.Arbitrum]: a('0x57f1887a8BF19b14fC0dF6Fd9B2acc9Af147eA85'),
  [Network.Sepolia]: a('0x57f1887a8bf19b14fc0df6fd9b2acc9af147ea85'),
  [Network.Base]: a('0x57f1887a8bf19b14fc0df6fd9b2acc9af147ea85')
});

/** UniswapV3FactoryUpgradeable */
({
  [Network.Mainnet]: a('0xa70e10beB02fF9a44007D9D3695d4b96003db101'),
  [Network.Sepolia]: a('0xDD2dce9C403f93c10af1846543870D065419E70b'),
  [Network.Arbitrum]: a('0xF4D0512FB47319B0CE9144EF582862e2921CaBF8'),
  [Network.Base]: '0x5FD88cD0034A0B2FC77E75Ea09b6e512511b0Eb9'
});

/** NFTXVaultFactoryUpgradableV3 */
({
  [Network.Mainnet]: a('0xC255335bc5aBd6928063F5788a5E420554858f01'),
  [Network.Sepolia]: a('0x31C56CaF49125043e80B4d3C7f8734f949d8178C'),
  [Network.Arbitrum]: a('0x4dEeb9D2Bff2e9C35ce1f013DcC4582F891cb711'),
  [Network.Base]: a('0x0D74B761eAB5cC7Cc0E4e625A2e2B8251A4915c6')
});

/** NftxFeeDistributorV3 */
({
  [Network.Mainnet]: a('0xF4d96C5094FCD9eC24E612585e723b58F89e21fe'),
  [Network.Sepolia]: a('0x66EF5B4b6ee05639194844CE4867515665F14fED'),
  [Network.Arbitrum]: a('0x0d50970C7848ebbE52661e70057D7D063B7de886'),
  [Network.Base]: a('0xA64c2F3f965f055E51482bF0960Ebb5f2904BF68')
});

/** A list of old fee distributors that are no longer active (but useful for historical data) */
({
  [Network.Mainnet]: [a('0x6845fF5f102bEF9D785468F0bEb535b4687406E7')]
});

/** A list of old pool routers that are no longer active but are still used to compute older positions based on the nfpm address */
({
  [Network.Sepolia]: {
    '0x55bdc76262b1e6e791d0636a0bc61cee23cdfa87': '0xd36ece08f76c50ec3f01db65bbc5ef5aa5fbe849'
  }
});
const ROOT = 'https://g.nftx.xyz';
const CHAINS = {
  [Network.Mainnet]: 'eth-mainnet',
  [Network.Arbitrum]: 'arbitrum-mainnet',
  [Network.Sepolia]: 'eth-sepolia',
  [Network.Base]: 'base-mainnet'
};
const createNftxGraphs = name => {
  return {
    [Network.Mainnet]: `${ROOT}/${name}/${CHAINS[Network.Mainnet]}`,
    [Network.Arbitrum]: `${ROOT}/${name}/${CHAINS[Network.Arbitrum]}`,
    [Network.Sepolia]: `${ROOT}/${name}/${CHAINS[Network.Sepolia]}`,
    [Network.Base]: `${ROOT}/${name}/${CHAINS[Network.Base]}`,
    '84532': `${ROOT}/${name}/base-sepolia`
  };
};
({
  [Network.Mainnet]: 'https://g.nftx.xyz/non-standard-nfts/eth-mainnet'
});
createNftxGraphs('erc721');
createNftxGraphs('erc1155');
createNftxGraphs('nftx-v3-vaults');
createNftxGraphs('nftx-v3-amm');
({
  [Network.Mainnet]: 'https://api.nftx.xyz/v3/eth-mainnet/quote',
  [Network.Sepolia]: 'https://api.nftx.xyz/v3/eth-sepolia/quote',
  [Network.Arbitrum]: 'https://api.nftx.xyz/v3/arb-mainnet/quote',
  [Network.Base]: 'https://api.nftx.xyz/v3/base-mainnet/quote'
});
({
  [Network.Mainnet]: 'https://data.nftx.xyz/vaultaprs',
  [Network.Arbitrum]: 'https://data.nftx.xyz/vaultaprs'
});
({
  [Network.Mainnet]: 'https://api.reservoir.tools',
  [Network.Arbitrum]: 'https://api-arbitrum.reservoir.tools',
  [Network.Sepolia]: 'https://api-sepolia.reservoir.tools',
  [Network.Base]: 'https://api-base.reservoir.tools',
  ['84532']: 'https://api-base-sepolia.reservoir.tools'
});
const WeiPerEther = 1000000000000000000n;
const Zero = 0n;
({
  [Network.Mainnet]: 36000,
  // 10 hours
  [Network.Arbitrum]: 36000,
  // 10 hours
  [Network.Sepolia]: 36000,
  // 1 hour
  [Network.Base]: 36000 // 10 hours
});

// The maximum range is 0.0001 ETH to 10,000 ETH

// 0.0001 ETH
({
  [Network.Mainnet]: 100000000000000n,
  [Network.Sepolia]: 100000000000000n,
  [Network.Arbitrum]: 100000000000000n,
  [Network.Base]: 100000000000000n
});
// 10,000 ETH
({
  [Network.Mainnet]: 10000000000000000000000n,
  [Network.Sepolia]: 10000000000000000000000n,
  [Network.Arbitrum]: 10000000000000000000000n,
  [Network.Base]: 10000000000000000000000n
});
({
  [Network.Mainnet]: -92160,
  [Network.Sepolia]: -92160,
  [Network.Arbitrum]: -92160,
  [Network.Base]: -92160
});
({
  [Network.Mainnet]: 92100,
  [Network.Sepolia]: 92100,
  [Network.Arbitrum]: 92100,
  [Network.Base]: 92100
});

/** Returns the spot price for buying a token */
const fetchSpotPrice = async ({
  tokenAddress,
  amount = WeiPerEther,
  network,
  quoteToken
}) => {
  // We start by attempting to quote 1 whole token, but if it fails (e.g. due to insufficient liquidity),
  // we can try to quote a smaller fraction of the token until we find a price that works.
  let fraction = WeiPerEther;
  let error = new UnknownError('Failed to fetch spot price');
  do {
    try {
      const quote = await fetchPrice({
        network,
        type: 'erc20',
        buyToken: tokenAddress,
        sellToken: quoteToken || 'ETH',
        buyAmount: fraction
      });

      // Extrapolate the price to determine the spot price for 1 whole token, then scale it by the desired amount.
      quote.price = quote.vTokenPrice = quote.price * amount / fraction;
      return quote;
    } catch (e) {
      error = e;
      fraction /= 100n;
    }
  } while (fraction > 0n);
  throw error;
};
var fetchSpotPrice$1 = fetchSpotPrice;

/** Returns the current price of ETH in USDC terms */
const fetchEthPrice = async ({
  network
}) => {
  const {
    price
  } = await fetchSpotPrice$1({
    network,
    tokenAddress: 'ETH',
    quoteToken: 'USDC'
  });
  return price;
};
var fetchEthPrice$1 = fetchEthPrice;

/** Returns the difference between a buy and sell of a single token */
const fetchSpread = async ({
  tokenAddress,
  network,
  quoteToken
}) => {
  try {
    const {
      price: buyPrice
    } = await fetchPrice({
      network,
      type: 'erc20',
      buyToken: tokenAddress,
      sellToken: quoteToken || 'ETH',
      buyAmount: WeiPerEther
    });
    const {
      price: sellPrice
    } = await fetchPrice({
      network,
      type: 'erc20',
      sellToken: tokenAddress,
      buyToken: quoteToken || 'ETH',
      sellAmount: WeiPerEther
    });
    return buyPrice - sellPrice;
  } catch {
    return Zero;
  }
};
var fetchSpread$1 = fetchSpread;

const fetchVTokenBalances = ({
  network = config.network,
  userAddress,
  vaultAddress,
  vaultId
}) => {
  return queryApi$1({
    url: `/${network}/balances`,
    query: {
      userAddress,
      vaultAddresses: vaultAddress,
      vaultIds: vaultId
    }
  });
};
var fetchVTokenBalances$1 = fetchVTokenBalances;

/** Get recent vault activity across all vaults */
const fetchActivity = ({
  network = config.network,
  fromTimestamp,
  vaultAddress,
  vaultAddresses,
  vaultId,
  vaultIds
}) => {
  if (fromTimestamp != null) {
    // Round timestamps to the nearest 60m
    fromTimestamp = Math.floor(Math.round(fromTimestamp / 3600) * 3600);
  }
  return queryApi$1({
    url: `/${network}/activity`,
    query: {
      fromTimestamp,
      ids: vaultAddress ? [vaultAddress] : vaultAddresses,
      vaultIds: vaultId ? [vaultId] : vaultIds
    }
  });
};
var fetchActivity$1 = fetchActivity;

const fetchVault = ({
  vaultId,
  network = config.network
}) => {
  return queryApi$1({
    url: `/${network}/vaults/${vaultId}`
  });
};
var fetchVault$1 = fetchVault;

const fetchVaults = ({
  vaultId,
  vaultIds,
  vaultAddress,
  vaultAddresses,
  manager,
  includeEmptyVaults,
  finalisedOnly,
  enabledOnly,
  network = config.network
}) => {
  return queryApi$1({
    url: `/${network}/vaults`,
    query: {
      enabledOnly,
      finalisedOnly,
      includeEmptyVaults,
      manager,
      id: vaultAddress ?? vaultAddresses,
      vaultId: vaultId ?? vaultIds
    }
  });
};
var fetchVaults$1 = fetchVaults;

/**
 * Flags a vault as stale and waits for the NFTX api to refetch the vault data
 */
const invalidateVault = async ({
  vaultId,
  network = config.network
}) => {
  await queryApi$1({
    url: `/${network}/vaults/${vaultId}`,
    method: 'POST'
  });
};
var invalidateVault$1 = invalidateVault;

/**
 * Flags a user as stale and waits for the NFTX api to refetch the user's positions and balances
 */
const invalidateUser = async ({
  network = config.network,
  userAddress
}) => {
  await queryApi$1({
    url: `/${network}/users/${userAddress}`,
    method: 'POST'
  });
};
var invalidateUser$1 = invalidateUser;

const fetchCollection = ({
  collectionAddress,
  network
}) => {
  return queryApi$1({
    url: `/${network}/collections/${collectionAddress}`
  });
};
var fetchCollection$1 = fetchCollection;

const fetchCollections = ({
  userAddress,
  mintable,
  network = config.network
}) => {
  return queryApi$1({
    url: `/${network}/collections`,
    query: {
      userAddress,
      mintable
    }
  });
};
var fetchCollections$1 = fetchCollections;

const quoteCreateInventoryPosition = ({
  network = config.network,
  ...query
}) => {
  return queryApi$1({
    url: `/${network}/quote/create-inventory-position`,
    method: 'POST',
    query
  });
};
var quoteCreateInventoryPosition$1 = quoteCreateInventoryPosition;

const quoteCreateLiquidityPosition = ({
  network = config.network,
  ...query
}) => {
  return queryApi$1({
    url: `/${network}/quote/create-liquidity-position`,
    method: 'POST',
    query
  });
};
var quoteCreateLiquidityPosition$1 = quoteCreateLiquidityPosition;

const quoteCreateVault = ({
  network = config.network,
  ...query
}) => {
  return queryApi$1({
    url: `/${network}/quote/create-vault`,
    method: 'POST',
    query
  });
};
var quoteCreateVault$1 = quoteCreateVault;

const quoteIncreaseLiquidity = ({
  network = config.network,
  ...query
}) => {
  return queryApi$1({
    url: `/${network}/quote/increase-liquidity`,
    method: 'POST',
    query
  });
};
var quoteIncreaseLiquidity$1 = quoteIncreaseLiquidity;

const quoteWithdrawInventory = ({
  network = config.network,
  ...query
}) => {
  return queryApi$1({
    url: `/${network}/quote/withdraw-inventory`,
    method: 'POST',
    query
  });
};
var quoteWithdrawInventory$1 = quoteWithdrawInventory;

const quoteWithdrawLiquidity = ({
  network = config.network,
  ...query
}) => {
  return queryApi$1({
    url: `/${network}/quote/withdraw-liquidity`,
    method: 'POST',
    query
  });
};
var quoteWithdrawLiquidity$1 = quoteWithdrawLiquidity;

export { fetchActivity$1 as fetchActivity, fetchAllAssets, fetchAsset$1 as fetchAsset, fetchAssets, fetchAuctions$1 as fetchAuctions, fetchCollection$1 as fetchCollection, fetchCollections$1 as fetchCollections, fetchEthPrice$1 as fetchEthPrice, fetchInventoryPool$1 as fetchInventoryPool, fetchInventoryPools$1 as fetchInventoryPools, fetchInventoryPosition$1 as fetchInventoryPosition, fetchInventoryPositions$1 as fetchInventoryPositions, fetchLiquidityPool$1 as fetchLiquidityPool, fetchLiquidityPools$1 as fetchLiquidityPools, fetchLiquidityPosition$1 as fetchLiquidityPosition, fetchLiquidityPositions$1 as fetchLiquidityPositions, fetchPrice, fetchQuote, fetchSpotPrice$1 as fetchSpotPrice, fetchSpread$1 as fetchSpread, fetchVTokenBalances$1 as fetchVTokenBalances, fetchVault$1 as fetchVault, fetchVaults$1 as fetchVaults, invalidateUser$1 as invalidateUser, invalidateVault$1 as invalidateVault, nsync, queryApi$1 as queryApi, quoteCreateInventoryPosition$1 as quoteCreateInventoryPosition, quoteCreateLiquidityPosition$1 as quoteCreateLiquidityPosition, quoteCreateVault$1 as quoteCreateVault, quoteIncreaseLiquidity$1 as quoteIncreaseLiquidity, quoteWithdrawInventory$1 as quoteWithdrawInventory, quoteWithdrawLiquidity$1 as quoteWithdrawLiquidity, streamAssets };
